import React, { useState } from 'react';
import { Box, Typography, Button, Container, Grid, Paper, TextField, CircularProgress, Chip, Stack, Card, CardContent, useTheme } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { sendChatRequest } from '../services/chatService';

interface ChatResponse {
  response: string;
  ads: Array<{
    id: number;
    title: string;
    description: string;
    category: string;
    target_keywords: string[];
    ctr: number;
  }>;
  keywords: string[];
}

const StyledFeatureCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  padding: '2rem',
  height: '100%',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const StyledIcon = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
}));

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Product = () => {
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState<ChatResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await sendChatRequest(prompt);
      setResponse(result);
    } catch (error) {
      console.error('Error:', error);
      setError('Error occurred while processing your request.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      title: 'Context-Aware Ad Placement',
      description: 'Our AI analyzes conversation context to place relevant ads naturally within responses.',
      icon: '🎯'
    },
    {
      title: 'Real-time Analytics',
      description: 'Track performance metrics, revenue, and user engagement in real-time.',
      icon: '📊'
    },
    {
      title: 'Easy Integration',
      description: 'Simple API integration with comprehensive documentation and support.',
      icon: '⚡'
    },
    {
      title: 'Revenue Optimization',
      description: 'Advanced algorithms maximize your revenue while maintaining user experience.',
      icon: '💰'
    }
  ];

  const benefits = [
    {
      title: 'For Publishers',
      items: [
        'Earn up to 85% revenue share',
        'Seamless integration with any LLM',
        'Real-time performance tracking',
        'Customizable ad placement rules'
      ]
    },
    {
      title: 'For Advertisers',
      items: [
        'Contextual ad targeting',
        'High engagement rates',
        'Detailed analytics dashboard',
        'Flexible budget control'
      ]
    }
  ];

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 64px)',
      marginTop: 0,
      background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
      color: 'white',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
      }
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '1.5rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Try Reasonic Demo
          </Typography>
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              color: '#94A3B8',
              maxWidth: '800px',
              margin: '0 auto',
              marginBottom: '3rem',
            }}
          >
            Experience how our AI-powered ad placement works in real-time
          </Typography>
        </motion.div>
      </Container>

      {/* Demo Section */}
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          color: 'white', 
          boxShadow: '0 4px 16px 0 rgba(0,0,0,0.25)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s ease',
        }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Enter your prompt here..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              sx={{
                mb: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3B82F6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3B82F6',
                  },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !prompt.trim()}
              sx={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                color: 'white',
                fontWeight: 700,
                borderRadius: '12px',
                px: 3,
                py: 1,
                boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
              endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            >
              Submit
            </Button>
          </form>
        </Paper>

        {error && (
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid rgba(220, 38, 38, 0.2)',
            borderRadius: '12px'
          }}>
            <Typography color="error" sx={{ color: '#F87171' }}>{error}</Typography>
          </Paper>
        )}

        {response && (
          <>
            <Paper sx={{ 
              p: 3, 
              mb: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 16px 0 rgba(0,0,0,0.18)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
                Response
              </Typography>
              <Typography sx={{ 
                whiteSpace: 'pre-wrap',
                color: 'white',
                lineHeight: 1.6
              }}>
                {response.response}
              </Typography>
            </Paper>

            <Paper sx={{ 
              p: 3, 
              mb: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 16px 0 rgba(0,0,0,0.18)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
                Extracted Keywords
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {response.keywords.map((keyword, index) => (
                  <Chip 
                    key={index} 
                    label={keyword} 
                    sx={{
                      bgcolor: 'rgba(30, 41, 59, 0.5)',
                      color: '#60A5FA',
                      borderColor: '#3B82F6',
                      fontWeight: 600,
                      letterSpacing: 0.2,
                      '&:hover': {
                        bgcolor: 'rgba(34, 48, 74, 0.5)',
                        color: 'white'
                      }
                    }}
                    variant="outlined" 
                  />
                ))}
              </Stack>
            </Paper>

            {response.ads.length > 0 && (
              <Paper sx={{ 
                p: 3, 
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 16px 0 rgba(0,0,0,0.18)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
                  Matched Advertisements
                </Typography>
                <Stack spacing={2}>
                  {response.ads.map((ad) => (
                    <Card 
                      key={ad.id} 
                      variant="outlined" 
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px 0 rgba(59,130,246,0.10)'
                        }
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: '#3B82F6', fontWeight: 700 }}>
                          {ad.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2 }}>
                          {ad.description}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {ad.target_keywords.map((keyword, index) => (
                            <Chip
                              key={index}
                              label={keyword}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(30, 41, 59, 0.5)',
                                color: '#60A5FA',
                                borderColor: '#3B82F6',
                                fontWeight: 600,
                                letterSpacing: 0.2,
                                '&:hover': {
                                  bgcolor: 'rgba(34, 48, 74, 0.5)',
                                  color: 'white'
                                }
                              }}
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Paper>
            )}
          </>
        )}
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '3rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Key Features
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div variants={fadeInUp}>
                  <StyledFeatureCard>
                    <StyledIcon>
                      <Typography variant="h4">{feature.icon}</Typography>
                    </StyledIcon>
                    <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                      {feature.description}
                    </Typography>
                  </StyledFeatureCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '3rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Benefits for Everyone
          </Typography>
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={6} key={index}>
                <StyledFeatureCard>
                  <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '2rem', color: '#FBBF24' }}>
                    {benefit.title}
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', padding: 0 }}>
                    {benefit.items.map((item, itemIndex) => (
                      <Box
                        component="li"
                        key={itemIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '1rem',
                          '&::before': {
                            content: '"✓"',
                            color: '#3B82F6',
                            marginRight: '1rem',
                            fontWeight: 'bold',
                          }
                        }}
                      >
                        <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </StyledFeatureCard>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 1) 100%)',
        padding: '6rem 0',
        position: 'relative',
        zIndex: 1,
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center' }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                marginBottom: '1.5rem',
                background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#94A3B8',
                marginBottom: '3rem',
              }}
            >
              Join our network of successful publishers and advertisers today
            </Typography>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  padding: '1rem 2.5rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  },
                }}
                onClick={() => navigate('/demo')}
              >
                Sign Up Now
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Product; 